/**
 * 流水默认时间窗面板（**N 域** · 用户级）—— 设置页里写 `user_preferences.default_txn_window` 的唯一 UI
 *
 * 依据：N 域 PRD **§5.2 ⑤** · **`AC-N2.18`**（可选 1/3/6 月，默认近 3 月 · 必测）·
 * 原型 `08-家庭与设置_原型.html`（「流水默认时间窗」行的 `<select>`）。
 *
 * ## 🔴 这个小设置为什么值得一个面板（它不是装饰）
 *
 * 「进入流水页默认拉多长」直接决定**首屏要扫多少行**（`AC-N2.19` 的网络审计项）。
 * 它同时是 C 域性能铁律（`DR-006`：首屏 ≤800ms）的**用户侧旋钮** ⇒ 面板必须把后果说清
 * （`windowEffectNote`），而不是只给一个下拉。
 *
 * ## 🔴 为什么没有「保存」按钮（与提醒偏好面板不同）
 *
 * 三档是**互斥单选**，一次选择就是一次完整决定（没有"改到一半"的中间态）⇒ 选中即保存。
 * （与 `ThemePreferencePanel` 同一条理由；提醒偏好是一组可组合开关，才需要显式保存。）
 */

import { useState } from 'react';
import { Alert, Card, CardContent, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TxnWindowPolicy, type TxnWindowValue } from '@jiaying/shared';
import { getPreferences, updatePreferences } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { Loading } from './Loading.js';
import { ScopeTag } from './ScopeTag.js';
import {
  WINDOW_DEFAULT_HINT,
  WINDOW_DESC,
  WINDOW_OPTIONS,
  WINDOW_SAVED_LABEL,
  WINDOW_SAVE_FAILED_PREFIX,
  WINDOW_SCOPE_NOTE,
  WINDOW_TITLE,
  windowEffectNote,
} from '../features/txn-window-labels.js';

export function TxnWindowPreferencePanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['preferences'], queryFn: getPreferences });

  /**
   * 用户刚选的那一档（`null` = 还没选过 ⇒ 显示服务端值）。
   * 🔴 渲染期派生（`override ?? 服务端值`）—— `renderToStaticMarkup` 不执行 effect
   * （`57` 批踩过：初值只放 effect 里会让首屏永远停在 Loading）。
   */
  const [override, setOverride] = useState<TxnWindowValue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (txnWindow: TxnWindowValue) => updatePreferences({ txnWindow }),
    onSuccess: (res) => {
      setOverride(res.txnWindow);
      setError(null);
      setSaved(true);
      // 🔴 让流水页的偏好缓存失效 —— 用户改完回到 `/journal` 应当按新的默认窗口拉
      void qc.invalidateQueries({ queryKey: ['preferences'] });
    },
    onError: (e: unknown) => {
      setOverride(null); // 失败 ⇒ 撤销这次选择，回到服务端值（不静默停在错的值上）
      setSaved(false);
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  if (q.isPending) return <Loading py={2} />;
  if (q.isError) return <Alert severity="error">偏好读取失败：{String(q.error)}</Alert>;

  const current: TxnWindowValue = override ?? q.data.txnWindow;

  const pick = (next: TxnWindowValue): void => {
    if (next === current) return;
    setSaved(false);
    setError(null);
    setOverride(next);
    save.mutate(next);
  };

  return (
    <Card data-anchor={WINDOW_TITLE} data-testid="txn-window-panel">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {WINDOW_TITLE}
          </Typography>
          <ScopeTag scope="用户级" />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div">
          {WINDOW_DESC}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" data-testid="txn-window-scope-note">
          {WINDOW_SCOPE_NOTE}
        </Typography>

        <TextField
          select
          size="small"
          label={WINDOW_TITLE}
          value={current}
          disabled={save.isPending}
          onChange={(e) => {
            pick(e.target.value as TxnWindowValue);
          }}
          sx={{ mt: 1.5, minWidth: 160 }}
          data-testid="txn-window-select"
        >
          {WINDOW_OPTIONS.map((o) => (
            <MenuItem key={o.id} value={o.id} data-testid={`txn-window-opt-${o.id}`}>
              {/* 🔴 「默认」标在 `TxnWindowPolicy.Default` 那一档（**不硬编码 '3m'** —— 口径只有一处） */}
              {o.id === TxnWindowPolicy.Default ? `${o.label}（${WINDOW_DEFAULT_HINT}）` : o.label}
            </MenuItem>
          ))}
        </TextField>

        {/* 🔴 把后果说出来（它是"首屏拉多长"的旋钮，不是装饰性偏好） */}
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mt: 0.5 }}
          data-testid="txn-window-effect"
        >
          {windowEffectNote(current)}
        </Typography>

        {saved ? (
          <Typography variant="caption" color="success.main" component="div" data-testid="txn-window-saved">
            {WINDOW_SAVED_LABEL}
          </Typography>
        ) : null}
        {error !== null ? (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="txn-window-error">
            {WINDOW_SAVE_FAILED_PREFIX}
            {error}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
